import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import { colorMap } from '@components/ChipSelector/ChipSelectorTsoPq';
import LoadingSpinnerTsoPq from '@components/LoadingSpinners/LoadingSpinnerTsoPq';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chart, type ChartOptions } from '@highcharts/react';
import { Box, styled, Typography } from '@mui/material';
import { tsoIds } from '@pages/MeasurementVisualization/Section/MeasurementVisualizationAppliedParametersTable';
import { usePostMeasurementGetSelectedQuery } from '@redux/apiSlices/measurementApi';
import { useGetMeasurementDeviceGetAllQuery } from '@redux/apiSlices/measurementDeviceApi';
import { useGetMeasurementParameterGetAllQuery } from '@redux/apiSlices/measurementParameterApi';
import type { measurementVisualizationActiveTableRowProps } from '@redux/slices/measurementVisualizationSlice';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type measurementVisualizationChartProps = {
  id: string;
  measurementDeviceTechnicalName: string;
  aggregationInterval: string;
  start: string;
  end: string;
  parameters: Array<string>;
  tsoId: number;
};

type MeasurementDataPoint = {
  time: string;
  [key: string]: string | number | null;
};

export const StyledBox = styled(Box)(() => {
  return {
    minWidth: '0px',
    boxSizing: 'border-box',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    background: tsoPqTheme.palette.background.other.color_other_background_white,
    padding: `2.25rem`, // todo change it component spacing values when spacinv values are added
    gap: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_page_gap_s,
    borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_4,
  };
});

export const StyledHeader = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'row',
    color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
    textAlign: 'center',
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_body_body_large,
    fontStyle: 'normal',
    fontWeight: 700,
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,
    lineHeight: tsoPqTheme.typography.lineHeight_xl,
    alignItems: 'center',
  };
});

export const StyledContent = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_page_gap_xxs,
  };
});

export const StyledTitleContainer = styled(Box)(() => {
  return {
    display: 'flex',
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_12,
    justifyContent: 'center',
    alignItems: 'center',
  };
});

export const StyledTitle = styled(Typography)(() => {
  return {
    color: tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_hover,
    textAlign: 'center',
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_button_button_large,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '2.25rem',
  };
});

export const StyledHeadedrTitle = styled(Typography)(() => {
  return {
    color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
    textAlign: 'center',
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_body_body_large,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: tsoPqTheme.typography.lineHeight_xl,
  };
});

export const StyledDescription = styled(Typography)(() => {
  return {
    color: tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled,
    textAlign: 'center',
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_body_body_large,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: tsoPqTheme.typography.lineHeight_xl,
  };
});

export const StyledTsoIndicator = styled('span', {
  shouldForwardProp: (prop) => !['tsoId'].includes(prop as string),
})(({ tsoId }: { tsoId: number }) => {
  const currentTso = tsoIds[tsoId as unknown as keyof typeof tsoIds] ?? 'defaultInactive';

  return {
    height: '1rem',
    width: '1rem',
    background: colorMap[currentTso as keyof typeof colorMap].active,
    display: 'flex',
    margin: 'auto 0',
    borderRadius: '50%',
  };
});

export const StyledOrderIndicator = styled(Typography)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'cente',
    alignItems: 'center',
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,
    padding: `${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4} ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8}`,
    borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_4,
    background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_lt,
    color: tsoPqTheme.palette.customTextColor.color_primary_font_ft_prim_accent,
    textAlign: 'center',
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_label_label_medium,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '1.3125rem',
  };
});

export const StyledIcon = styled(FontAwesomeIcon)(() => {
  return {
    color: tsoPqTheme.palette.customError.color_signal_error_error,
    fontSize: '1.375rem',
  };
});

const StyledLoadingSpinnerTsoPq = styled(LoadingSpinnerTsoPq)(() => {
  return {
    margin: 'auto',
  };
});

const MeasurementVisualizationChart = (props: Readonly<measurementVisualizationActiveTableRowProps>): ReactElement => {
  const { t } = useTranslation();

  const { data: measurements, isLoading } = usePostMeasurementGetSelectedQuery({
    measurementDeviceTechnicalName: props.measurementDeviceTechnicalName,
    aggregationInterval: props.aggregationIntervalType,
    tsoId: props.tsoId,
    from: props.start,
    to: props.end,
    body: props.parameters,
  });

  const { data: measurementParameters } = useGetMeasurementParameterGetAllQuery();
  const { data: measurementDevices } = useGetMeasurementDeviceGetAllQuery();

  const measurementSeries = useMemo<MeasurementDataPoint[]>(() => {
    return Array.isArray(measurements) ? (measurements as MeasurementDataPoint[]) : [];
  }, [measurements]);

  const hasMeasurements = measurementSeries.length > 0;

  const hasPlottableValues = useMemo(() => {
    if (!measurementSeries.length) {
      return false;
    }

    return measurementSeries.some((measurement) =>
      Object.entries(measurement).some(([key, value]) => key !== 'time' && value !== null && value !== undefined)
    );
  }, [measurementSeries]);

  const chartData = useMemo(() => {
    if (measurementSeries.length > 0) {
      return Object.keys(measurementSeries[0])
        .filter((x) => x !== 'time')
        .map((key) => ({
          name: `${t(measurementParameters?.externalDtos?.find((x) => x.technicalName === key)?.name ?? '') ?? ''} ${key}`,
          type: 'line',
          data: measurementSeries.map((x) => [x.time, x[key]]),
        }));
    }
  }, [measurementParameters?.externalDtos, measurementSeries, t]);

  const yAxisTitleText = useMemo(() => {
    if (!measurementSeries.length) return '';

    const technicalNames = Object.keys(measurementSeries[0]);

    const voltCategories = new Set<string>();
    const percentCategories = new Set<string>();
    const ampereCategories = new Set<string>();
    const degreeCategories = new Set<string>();
    const unitlessCategories = new Set<string>();

    technicalNames.forEach((mp) => {
      if (mp.startsWith('u')) {
        if (/^u.{1,2}$/.test(mp)) {
          voltCategories.add('Spannungseffektivwerte');
        } else if (/^u.{1,2}h1$/.test(mp)) {
          voltCategories.add('Spannungsharmonische 1. Ordnung');
        } else if (/^uextru.*/.test(mp)) {
          voltCategories.add('Spannungsextremwerte');
        } else if (/^u[1-3]{1,2}m$/.test(mp)) {
          voltCategories.add('Betrag von Phasor');
        } else if (/^u.{1,2}h([2-9]|[1-5][0-9])?$/.test(mp)) {
          percentCategories.add('Spannungsharmonische');
        } else if (/^u.{1,2}ih[0-9]{1,2}$/.test(mp)) {
          percentCategories.add('Spannungszwischenharmonische');
        } else if (/^u.{1,2}_thd$/.test(mp)) {
          percentCategories.add('THD');
        } else if (mp === 'u_u2') {
          percentCategories.add('Unsymmetrie');
        } else if (/^u[1-3]{1,2}p$/.test(mp)) {
          degreeCategories.add('Phase von Phasor');
        } else if (/^u.{1,2}hp[0-9]{1,2}$/.test(mp)) {
          degreeCategories.add('Winkel der Spannungsharmonischen');
        } else if (/^ustatu/.test(mp)) {
          unitlessCategories.add('Kurzzeitflicker');
        } else if (/^upltu/.test(mp)) {
          unitlessCategories.add('Langzeitflicker');
        }
      }
      if (mp.startsWith('i')) {
        if (/^i[1-3]$/.test(mp)) {
          ampereCategories.add('Stromeffektivwerte');
        } else if (/^i[1-3]h[0-9]{1,2}$/.test(mp)) {
          ampereCategories.add('Stromharmonische');
        } else if (/^i[1-3]ih[0-9]{1,2}$/.test(mp)) {
          ampereCategories.add('Stromzwischenharmonische');
        } else if (/^i[1-3]hp[0-9]{1,2}$/.test(mp)) {
          degreeCategories.add('Winkel der Stromharmonischen');
        } else if (/^i[1-3]_thd$/.test(mp)) {
          percentCategories.add('THD');
        }
      }
    });

    const categories: string[] = [];
    if (voltCategories.size) categories.push([...voltCategories].join(', ') + ' [V]');
    if (percentCategories.size) categories.push([...percentCategories].join(', ') + ' [%]');
    if (ampereCategories.size) categories.push([...ampereCategories].join(', ') + ' [A]');
    if (degreeCategories.size) categories.push([...degreeCategories].join(', ') + ' [°]');
    if (unitlessCategories.size) categories.push([...unitlessCategories].join(', '));

    return categories.join('; ');
  }, [measurementSeries]);

  const title = measurementDevices?.externalDtos?.find(
    (d) => d.technicalName == props.measurementDeviceTechnicalName
  )?.name;

  const tsoId =
    measurementDevices?.externalDtos?.find((d) => d.technicalName == props.measurementDeviceTechnicalName)?.tsoId ??
    props.tsoId;

  if (hasMeasurements && hasPlottableValues) {
    const options: ChartOptions = {
      chart: {
        type: 'line',
        height: 400,
        backgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
        plotBackgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
        zooming: {
          type: 'x' as const,
        },
      },
      title: {
        useHTML: true,
        text: ``,
        align: 'left',
      },
      xAxis: {
        type: 'datetime',
        title: { text: 'Time' },
        labels: {
          format: '{value:%d.%m.%Y %H:%M}',
        },
      },
      yAxis: {
        title: { text: yAxisTitleText },
      },
      tooltip: {
        shared: true,
        xDateFormat: '%d.%m.%Y %H:%M',
      },
      credits: {
        enabled: false,
      },
      series: chartData,
    };
    return (
      <>
        {isLoading ? (
          <StyledLoadingSpinnerTsoPq />
        ) : (
          <StyledBox key={props.order}>
            <StyledHeader>
              <StyledOrderIndicator>{props.order}</StyledOrderIndicator>
              <StyledTsoIndicator tsoId={tsoId}></StyledTsoIndicator>
              <StyledHeadedrTitle>{title}</StyledHeadedrTitle>
            </StyledHeader>
            <StyledContent>
              <Chart options={options} />
            </StyledContent>
          </StyledBox>
        )}
      </>
    );
  }

  return (
    <>
      {isLoading ? (
        <StyledLoadingSpinnerTsoPq />
      ) : (
        <StyledBox key={props.order}>
          <StyledHeader>
            <StyledOrderIndicator>{props.order}</StyledOrderIndicator>
            <StyledTsoIndicator tsoId={tsoId}></StyledTsoIndicator>
            <StyledHeadedrTitle>{title}</StyledHeadedrTitle>
          </StyledHeader>
          <StyledContent>
            <StyledTitleContainer>
              <StyledIcon icon={faCircleExclamation} />
              <StyledTitle>
                {hasMeasurements
                  ? t('measurementVisualization.chart.error.onlyNullValuesTitle')
                  : t('general.error.noDataFound')}
              </StyledTitle>
            </StyledTitleContainer>
            <StyledDescription>
              {hasMeasurements
                ? t('measurementVisualization.chart.error.onlyNullValuesDescription')
                : t('measurementVisualization.chart.error.noDataDescription')}
            </StyledDescription>
          </StyledContent>
        </StyledBox>
      )}
    </>
  );
};

export default MeasurementVisualizationChart;
